/**
 * @ignore
 * Created by shanie on 3/8/17.
 */

import { useState, lazy, Suspense } from 'react';
import { useIntl, defineMessages, FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import PropTypes from 'prop-types';

import LikeHeartIcon from 'dibs-icons/exports/LikeHeartIcon';
import { Link } from 'dibs-elements/exports/Link';

// Styles
import classnames from 'classnames';
import styles from './Heart-Styles.scss';

const HeartPulseLazy = lazy(() => import(/* webpackChunkName: "HeartPulse" */ './HeartPulse'));

const messages = defineMessages({
    label: {
        id: 'dibs-portfolio-heart.HeartIconWrapper.label',
        defaultMessage: 'Save to favorites',
    },
});

const HeartCount = ({ count }) => {
    let value = <FormattedNumber value={count} />;
    if (count > 999) {
        // If count is greater than 1000, display as value in thousands up to 999.9K
        const countInThousands = Math.min(999.9, count / 1000);
        // Round to tenth decimal
        const countToTenthDecimal = Math.round(countInThousands * 10) / 10;
        // Format number value
        const formattedCount = <FormattedNumber value={countToTenthDecimal} />;
        // Format value in thousands
        value = (
            <FormattedMessage
                id="HeartIcon.HeartCount.value"
                defaultMessage="{count}K"
                values={{ count: formattedCount }}
            />
        );
    }

    return <div className={styles.heartCount}>{value}</div>;
};

HeartCount.propTypes = {
    count: PropTypes.number.isRequired,
};

const HeartIconWrapper = props => {
    const intl = useIntl();

    const [isActive, setIsActive] = useState(false);
    const className = classnames(styles.heart, props.className, {
        [styles[props.type]]: props.type,
    });
    const iconClasses = classnames(styles.icon, {
        [styles.isStrokeWidthHeavy]: props.isStrokeWidthHeavy,
        [styles.hoverClass]: props.useHoverClass,
        [styles.dark]: props.theme === 'dark',
        [styles.light]: props.theme === 'light',
    });

    const dataTn = props.isFavorited ? 'item-saved-icon' : 'item-save-icon';
    const id = `${props.itemId}-heart`;

    function toggleActive(active) {
        if (props.type === 'button') {
            setIsActive(active);
        }
    }

    const showAsDiv = props.wrapperComponent === 'div';
    let Wrapper;
    switch (props.wrapperComponent) {
        case 'div':
            Wrapper = 'div';
            break;
        case 'Link':
            Wrapper = Link;
            break;
    }

    const sharedWrapperProps = {
        className: classnames(
            styles.iconWrapper,
            {
                [styles[props.type]]: props.type,
                [styles.isActive]: isActive,
            },
            props.buttonClass
        ),
    };

    return (
        <>
            <Wrapper
                {...(showAsDiv
                    ? { ...sharedWrapperProps }
                    : {
                          ariaLabel: intl.formatMessage(messages.label),
                          onClick: props.onClick,
                          onTouchStart: () => toggleActive(true),
                          onTouchEnd: () => toggleActive(false),
                          ...sharedWrapperProps,
                      })}
            >
                {/* Carefully with data-tn. it must always stay together with id. It's required for heart status detection. */}
                <div
                    className={className}
                    onAnimationEnd={props.onAnimationEnd}
                    data-tn={dataTn}
                    id={id}
                >
                    {props.animateHeartPulse && (
                        <Suspense fallback="">
                            <HeartPulseLazy isFilled={props.isFilled} />
                        </Suspense>
                    )}
                    <LikeHeartIcon
                        className={iconClasses}
                        animateHeartBeat={props.isAnimated}
                        isFilled={props.isFilled}
                        theme={props.theme}
                    />
                </div>
            </Wrapper>
            {props.heartCount > 0 && !props.hideTotalLikesCount && (
                <HeartCount count={props.heartCount} />
            )}
        </>
    );
};

HeartIconWrapper.defaultProps = {
    isFavorited: false,
    type: 'outline',
    isStrokeWidthHeavy: false,
    useHoverClass: false,
    hideTotalLikesCount: false,
    wrapperComponent: 'Link',
};

HeartIconWrapper.propTypes = {
    className: PropTypes.string,
    buttonClass: PropTypes.string,
    isStrokeWidthHeavy: PropTypes.bool,
    isAnimated: PropTypes.bool,
    isFavorited: PropTypes.bool,
    isFilled: PropTypes.bool,
    useHoverClass: PropTypes.bool,
    onClick: PropTypes.func,
    onAnimationEnd: PropTypes.func,
    wrapperComponent: PropTypes.oneOf(['div', 'Link']),
    hideTotalLikesCount: PropTypes.bool,
    type: PropTypes.oneOf(['outline', 'button']),
    itemId: PropTypes.string,
    theme: PropTypes.oneOf(['dark', 'light']),
    heartCount: PropTypes.number,
    animateHeartPulse: PropTypes.bool,
};

export default HeartIconWrapper;
